/**
 * PATH: packages/ui/src/components/settings/entities-tab/entities-tab-types.ts
 * PURPOSE: Shared types and constants for the EntitiesTab feature
 * WHY: Extracted from EntitiesTab.tsx to keep each file focused and under 300 lines
 *
 * DEPENDENCIES:
 *   - ../types: SettingsApiClient contract
 */

import type { SettingsApiClient } from '../types'

export interface OrganisationEntity {
  id: number
  name: string
  legal_name?: string
  code?: string
  abn?: string
  color?: string
  xero_tenant_id?: string | null
  xero_tenant_name?: string | null
  is_default?: boolean
  is_active?: boolean
  job_count?: number
}

export interface XeroTenant {
  id: string
  name: string
  is_linked?: boolean
}

export interface EntitiesTabProps {
  apiClient?: SettingsApiClient
  organisationId?: string
  /** Whether Xero is connected (enables tenant mapping UI) */
  isXeroConnected?: boolean
  /**
   * Whether Xero accounting is APPLICABLE to this organisation at all
   * (maps to OrganisationSettings.enable_xero). Defaults to true. When
   * false, the Xero tenant-mapping controls + "Connect Xero" CTA are
   * hidden and the entity list shows without Xero columns — orgs on
   * QuickBooks / MYOB / no accounting shouldn't be funnelled into Xero
   * UI. Entity CRUD and Corporate structure stay available.
   */
  xeroApplicable?: boolean
  /** Callback to navigate to Integrations tab to connect Xero */
  onConnectXero?: () => void
  className?: string
}

export interface EntityFormState {
  name: string
  legal_name: string
  code: string
  abn: string
  color: string
}

export const DEFAULT_FORM: EntityFormState = {
  name: '',
  legal_name: '',
  code: '',
  abn: '',
  color: '#6366f1',
}

// ── Corporate-structure mode (EntityStructurePanel + buildEntityTree) ────────
// Mirrors the `/organisations/:id/entities/structure/` API response shape.

/** One node in the group/ownership tree. */
export interface EntityStructureNode {
  id: number
  name: string
  color?: string
  /** 'holding' | 'operating' | 'mixed' — kept loose to match entityKindLabel. */
  entity_kind?: string
  parent_entity_id?: number | null
  /** Ownership of the parent entity, as a percentage. */
  ownership_percent?: number | null
  is_ultimate_holding?: boolean
  children?: EntityStructureNode[]
}

/** An ultimate-shareholder row on the group's holding entity (cap table). */
export interface EntityShareholder {
  id: number
  /** FK to the entity this shareholder belongs to. */
  entity: number
  name: string
  shareholder_kind: 'individual' | 'company' | 'trust' | 'partnership' | 'other'
  ownership_percent: number
  is_ultimate_shareholder?: boolean
}

/** Response payload for the corporate-structure endpoint. */
export interface EntityStructureResponse {
  entities: EntityStructureNode[]
  /** Nested tree form used by the group-structure view. */
  tree?: EntityStructureNode[]
  ultimate_holding_id?: number | null
  shareholders?: EntityShareholder[]
  shareholders_total_percent?: number
  shareholders_complete?: boolean
}
